<template>
    <div v-if="isAiChatbotReady" :class="customClass">
        <section
            class="ai-chatbot-starter bg-white p-2"
            :aria-label="regionLabel"
        >
            <div class="d-flex align-items-center">
                <span class="icon icon-ai-generative ai-chatbot-starter__icon me-1" aria-hidden="true" />
                <h2 class="h4 mb-0">
                    {{ dto.title }}
                </h2>
            </div>

            <hr class="separator separator--gray4 my-1" role="presentation" aria-hidden="true">

            <ul class="list-unstyled d-flex flex-column flex-md-row flex-md-wrap align-items-start gap-1 mb-2">
                <li v-for="(suggestedPrompt, index) in dto.suggestedPrompts" :key="'ai-chatbot-starter-prompt-' + index">
                    <span class="ob1-filterchip ob1-choicechip">
                        <button
                            type="button"
                            class="ob1-filterchip-label"
                            @click="onChipClick(suggestedPrompt)"
                        >
                            {{ suggestedPrompt }}
                        </button>
                    </span>
                </li>
            </ul>

            <div class="d-flex justify-content-center justify-content-md-start">
                <button
                    type="button"
                    class="btn btn-secondary d-inline-flex align-items-center"
                    :aria-label="newConversationAriaLabel"
                    @click="onNewConversationClick()"
                >
                    <span class="icon icon-ai-generative me-1" aria-hidden="true" />
                    {{ dto.newConversationLabel }}
                </button>
            </div>
        </section>
    </div>
</template>

<script lang="ts">
import {defineComponent, PropType} from "vue";
import {AiChatbotStarterDto} from "../models/AiChatbotStarterDto";
import {AiChatbotService} from "../services/AiChatbotService";
import {AiChatbotTrackingService} from "../services/AiChatbotTrackingService";

// Délai au-delà duquel le chatbot est considéré comme introuvable
const AI_CHATBOT_READY_TIMEOUT_MS = 5000;

const REGION_LABEL = "Faites vous accompagner par Sharlie votre conseiller IA";
const NEW_CONVERSATION_ARIA_SUFFIX = "votre conseiller IA. Ouvrir Sharlie";

export default defineComponent({
    name: "AiChatbotStarter",
    props: {
        dto: {
            type: Object as PropType<AiChatbotStarterDto>,
            required: true
        },
        customClass: {
            type: String,
            default: "",
        },
    },
    data() {
        return {
            isAiChatbotReady: AiChatbotService.isReady(),
            isImpressionTracked: false,
            stopListeningAiChatbot: null as (() => void) | null,
            aiChatbotReadyTimeoutId: null as number | null,
        };
    },
    computed: {
        regionLabel(): string {
            return REGION_LABEL;
        },
        newConversationAriaLabel(): string {
            return `${this.dto.newConversationLabel}, ${NEW_CONVERSATION_ARIA_SUFFIX}`;
        },
    },
    watch: {
        isAiChatbotReady: {
            immediate: true,
            handler(isReady: boolean) {
                if (isReady && !this.isImpressionTracked) {
                    this.isImpressionTracked = true;
                    AiChatbotTrackingService.trackImpression();
                }
            }
        }
    },
    mounted() {
        this.stopListeningAiChatbot = AiChatbotService.onReadyChange((isReady: boolean) => {
            this.isAiChatbotReady = isReady;
        });
        this.aiChatbotReadyTimeoutId = window.setTimeout(() => {
            if (!this.isAiChatbotReady) {
                AiChatbotTrackingService.trackLoadingError();
            }
        }, AI_CHATBOT_READY_TIMEOUT_MS);
    },
    beforeDestroy() {
        this.stopListeningAiChatbot?.();
        if (this.aiChatbotReadyTimeoutId !== null) {
            window.clearTimeout(this.aiChatbotReadyTimeoutId);
        }
    },
    methods: {
        onChipClick(prompt: string): void {
            AiChatbotTrackingService.trackChipClick(prompt);
            this.startConversation(prompt);
        },
        onNewConversationClick(): void {
            AiChatbotTrackingService.trackButtonClick(this.dto.newConversationLabel);
            this.startConversation();
        },
        async startConversation(prompt?: string): Promise<void> {
            const success = await AiChatbotService.startConversation(prompt);
            if (!success) {
                console.warn("AI Chatbot : le message n'a pas pu être envoyé.");
            }
        },
    },
});
</script>

<style scoped lang="scss">
.ai-chatbot-starter {
    border: 1px solid var(--bs-primary);
    border-radius: 0.5rem;
}

.ob1-sosh .ai-chatbot-starter {
    border-color: var(--bs-primary3);
}

.ai-chatbot-starter__icon {
    font-size: 2.5rem;
}
</style>
